import { useEffect, useRef, useState } from 'react';
import Daily from '@daily-co/daily-js';
import toast from 'react-hot-toast';
import { useLocalization } from '../../context/LocalizationContext';
import { formatTime } from '../../utils/helpers';
import './MeetingSDK.css';

import phoneIcon from '../../assets/icons/newDesign/phone.svg';
import micIcon from '../../assets/icons/AI/whiteMic.svg';
import cameraIcon from '../../assets/icons/newDesign/camera.svg';
import mutedMicIcon from '../../assets/icons/newDesign/mutedMic.svg';
import cameraOffIcon from '../../assets/icons/newDesign/cameraOff.svg';

const MeetingSDK = ({
  roomUrl,
  onError,
  onJoined,
  onCallEnd,
  userData,
  onLoadingStateChange
}) => {
  const { t } = useLocalization();
  const callObjectRef = useRef(null);
  const remoteContainerRef = useRef(null);
  const localPiPRef = useRef(null);
  const videoElementsRef = useRef({}); // Track existing video elements
  const [isJoined, setIsJoined] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionDuration, setSessionDuration] = useState(0);
  const [participantCount, setParticipantCount] = useState(0);
  const [remoteParticipants, setRemoteParticipants] = useState([]);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isVideoMuted, setIsVideoMuted] = useState(false);

  // Timer effect - only start when participants > 1 (doctor has joined)
  useEffect(() => {
    let interval = null;
    if (isJoined && participantCount > 1) {
      interval = setInterval(() => {
        setSessionDuration(prev => prev + 1);
      }, 1000);
    } else if (!isJoined) {
      // Reset timer when call ends
      setSessionDuration(0);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isJoined, participantCount]);

  // formatTime is imported from helpers.ts

  // Updated utility function - only creates if doesn't exist, updates tracks if changed
  const getOrCreateVideoElement = (parentElement, videoTrack, audioTrack, sessionId) => {
    let videoEl = videoElementsRef.current[sessionId];

    // Create new element only if it doesn't exist
    if (!videoEl) {
      videoEl = document.createElement('video');
      videoEl.id = `video-${sessionId}`;
      videoEl.autoplay = true;
      videoEl.playsInline = true;

      // The local video needs to be muted
      if (sessionId === 'local') {
        videoEl.muted = true;
      }

      parentElement.appendChild(videoEl);
      videoElementsRef.current[sessionId] = videoEl;
    } else {
      // Move element to correct parent if it's not already there
      if (videoEl.parentElement !== parentElement) {
        parentElement.appendChild(videoEl);
      }
    }

    // Only update srcObject if tracks have changed
    const tracks = [];
    if (videoTrack) tracks.push(videoTrack);
    if (audioTrack && sessionId !== 'local') tracks.push(audioTrack);

    const currentStream = videoEl.srcObject;
    const needsUpdate = !currentStream ||
      currentStream.getTracks().length !== tracks.length ||
      !tracks.every(track => currentStream.getTracks().includes(track));

    if (needsUpdate && tracks.length > 0) {
      videoEl.srcObject = new MediaStream(tracks);
    }

    return videoEl;
  };

  // Function to manage video elements - no longer destroys everything
  const updateParticipantCount = () => {
    if (!callObjectRef.current) return;
    const participants = callObjectRef.current.participants();
    const remote = Object.values(participants).filter(p => !p.local);
    setRemoteParticipants(remote);
    setParticipantCount(Object.keys(participants).length);

    // Sync local mute states
    const local = participants.local;
    if (local) {
      setIsAudioMuted(!local.audio);
      setIsVideoMuted(!local.video);
    }
  };

  const updateVideos = () => {
    if (!callObjectRef.current || !remoteContainerRef.current || !localPiPRef.current) return;

    const participants = callObjectRef.current.participants();
    const activeSessionIds = new Set();

    Object.values(participants).forEach((participant) => {
      const videoTrack = participant.tracks?.video?.track;
      const audioTrack = participant.tracks?.audio?.track;
      const sessionId = participant.local ? 'local' : participant.session_id;

      activeSessionIds.add(sessionId);

      // Render if there's video OR audio
      if (videoTrack || audioTrack) {
        const isLocal = participant.local;
        const container = isLocal ? localPiPRef.current : remoteContainerRef.current;

        if (!isLocal) {
          // Check if wrapper already exists for this remote participant
          let wrapper = container.querySelector(`[data-session-id="${sessionId}"]`);
          if (!wrapper) {
            wrapper = document.createElement('div');
            wrapper.className = 'remote-video-wrapper';
            wrapper.setAttribute('data-session-id', sessionId);
            container.appendChild(wrapper);
          }
          getOrCreateVideoElement(wrapper, videoTrack, audioTrack, sessionId);
        } else {
          getOrCreateVideoElement(container, videoTrack, audioTrack, sessionId);
        }
      }
    });

    // Remove video elements and wrappers for participants who left
    Object.keys(videoElementsRef.current).forEach(sessionId => {
      if (!activeSessionIds.has(sessionId)) {
        const videoEl = videoElementsRef.current[sessionId];
        if (videoEl) {
          videoEl.srcObject = null;
          // If it's a remote participant, remove the wrapper too
          if (sessionId !== 'local') {
            const wrapper = videoEl.parentElement;
            if (wrapper && wrapper.getAttribute('data-session-id') === sessionId) {
              wrapper.parentElement?.removeChild(wrapper);
            }
          } else {
            videoEl.parentElement?.removeChild(videoEl);
          }
        }
        delete videoElementsRef.current[sessionId];
      }
    });
  };

  // Setup call and join with better audio settings
  const setupCall = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Create the Daily Call Object with audio quality settings
      callObjectRef.current = Daily.createCallObject({
        audioSource: true,
        videoSource: true,
      });

      // Set up event listeners
      callObjectRef.current
        .on('joined-meeting', () => {
          setIsJoined(true);
          setIsLoading(false);
          updateParticipantCount();
          updateVideos();
          onJoined?.(); // Corrected: call joined callback
        })

        .on('participant-joined', () => {
          updateParticipantCount();
          updateVideos();
        })
        .on('participant-updated', updateVideos)
        .on('participant-left', () => {
          updateParticipantCount();
          updateVideos();
        })
        .on('track-started', updateVideos) // Add this for better track handling
        .on('left-meeting', () => {
          setIsJoined(false);
        })
        .on('camera-error', (event) => {
          console.error('Camera/Mic error:', event);
        })
        .on('error', (error) => {
          setError(error.message || t('meetingSDK.errorOccurred'));
          onError?.(error.message || t('meetingSDK.errorOccurred'));
        });

      // Join the call with better settings
      await callObjectRef.current.join({
        url: roomUrl,
        audioSource: true,
        videoSource: true,
        userName: userData?.name || "Patient",
      });

      // Set input settings for better audio quality
      await callObjectRef.current.setInputDevicesAsync({
        audioSource: true,
        videoSource: true,
      });

      // Enable audio and video
      try {
        await callObjectRef.current.setLocalAudio(true);
      } catch (e) { }
      try {
        await callObjectRef.current.setLocalVideo(true);
      } catch (e) { }

    } catch (err) {
      setError(err.message || t('meetingSDK.failedToJoin'));
      onError?.(err.message || t('meetingSDK.failedToJoin'));
      setIsLoading(false);
    }
  };

  // Control handlers
  const toggleAudio = async () => {
    if (!callObjectRef.current) return;
    try {
      const targetMuted = !isAudioMuted;
      // Optimistically update the UI to feel responsive
      setIsAudioMuted(targetMuted);

      await callObjectRef.current.setLocalAudio(!targetMuted);

      // Give it a brief moment to update state internally before checking track existence
      setTimeout(() => {
        if (!callObjectRef.current) return;
        const local = callObjectRef.current.participants().local;
        if (!targetMuted && local && !local.audioTrack) {
          toast.error(t('meetingSDK.enableMic'));
          setIsAudioMuted(true);
        }
      }, 500);

    } catch (error) {
      console.error('Toggle audio error:', error);
      setIsAudioMuted(true);
    }
  };

  const toggleVideo = async () => {
    if (!callObjectRef.current) return;
    try {
      const targetMuted = !isVideoMuted;
      // Optimistically update the UI to feel responsive
      setIsVideoMuted(targetMuted);

      await callObjectRef.current.setLocalVideo(!targetMuted);

      // Give it a brief moment to update state internally before checking track existence
      setTimeout(() => {
        if (!callObjectRef.current) return;
        const local = callObjectRef.current.participants().local;
        if (!targetMuted && local && !local.videoTrack) {
          toast.error(t('meetingSDK.enableCamera'));
          setIsVideoMuted(true);
        }
      }, 500);

    } catch (error) {
      console.error('Toggle video error:', error);
      setIsVideoMuted(true);
    }
  };

  const handleEndCall = () => {
    cleanup();
    onCallEnd?.(); // This triggers navigation/cleanup in parent
  };

  // Cleanup function
  const cleanup = () => {
    // Clean up all video elements
    Object.values(videoElementsRef.current).forEach(videoEl => {
      if (videoEl && videoEl.srcObject) {
        videoEl.srcObject.getTracks().forEach(track => track.stop());
        videoEl.srcObject = null;
      }
      if (videoEl && videoEl.parentElement) {
        videoEl.parentElement.removeChild(videoEl);
      }
    });
    videoElementsRef.current = {};

    if (callObjectRef.current) {
      callObjectRef.current.leave();
      callObjectRef.current.destroy();
      callObjectRef.current = null;
    }
  };

  // Initialize call on mount
  useEffect(() => {
    setupCall();

    // Cleanup on unmount
    return () => {
      cleanup();
    };
  }, [roomUrl]);

  // Handle window beforeunload to ensure proper cleanup
  useEffect(() => {
    const handleBeforeUnload = () => {
      cleanup();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);



  // Fix: Ensure videos are updated once the container elements are rendered and state is ready
  useEffect(() => {
    if (isJoined && !isLoading && participantCount > 1) {
      // Use a small timeout to ensure refs are populated after the state-driven re-render
      const timeout = setTimeout(() => {
        updateVideos();
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [isJoined, isLoading, participantCount]);

  // Report loading state to parent
  useEffect(() => {
    if (onLoadingStateChange) {
      const isCurrentlyLoading = isLoading || (isJoined && participantCount <= 1);
      onLoadingStateChange(isCurrentlyLoading);
    }
  }, [isLoading, isJoined, participantCount, onLoadingStateChange]);

  if (error) {
    return (
      <div className="meeting-error">
        <h3>{t('meetingSDK.callError')}</h3>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>
          {t('meetingSDK.retry')}
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="meeting-loading">
        <p>{t('meetingSDK.joiningCall')}</p>
        <div className="loading-spinner"></div>
      </div>
    );
  }
  if (isJoined && participantCount <= 1) {
    return (
      <div className="meeting-loading">
        <p>{t('meetingSDK.connectingToDoctor')}</p>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="meeting-sdk">
      {/* Meeting Info Bar (Top) */}
      {isJoined && (
        <div className="meeting-info-bar">
          <div className="meeting-info-text">
            {t('meetingSDK.sessionWithDr')}
          </div>
          <div className="meeting-info-time">
            {formatTime(sessionDuration)}
          </div>
        </div>
      )}

      {/* Wrapper for the centered video content */}
      <div className="meeting-video-area">
        {/* Container for remote participants (the background) */}
        <div
          ref={remoteContainerRef}
          className={`remote-videos-container ${remoteParticipants.length > 1 ? 'grid-view' : ''}`}
        />

        {/* Container for the local participant (the small box in front) */}
        <div
          ref={localPiPRef}
          className="local-video-pip"
          id="local-video-pip"
        />
      </div>

      {/* Control Bar (Bottom) */}
      {isJoined && (
        <div className="meeting-controls">
          <button
            className={`control-btn ${isVideoMuted ? 'muted' : ''}`}
            onClick={toggleVideo}
            title={isVideoMuted ? "Start Video" : "Stop Video"}
          >
            <img src={isVideoMuted ? cameraOffIcon : cameraIcon} alt="Camera" />
          </button>

          <button
            className={`control-btn ${isAudioMuted ? 'muted' : ''}`}
            onClick={toggleAudio}
            title={isAudioMuted ? "Unmute" : "Mute"}
          >
            <img src={isAudioMuted ? mutedMicIcon : micIcon} alt="Microphone" />
          </button>

          <button
            className="control-btn end-call"
            onClick={handleEndCall}
            title="End Call"
          >
            <img src={phoneIcon} alt="End Call" />
          </button>
        </div>
      )}
    </div>
  );
};

export default MeetingSDK;